import { supabase } from '../lib/supabase';

interface HubSpotConfig {
  apiKey: string;
  portalId: string;
}

interface HubSpotContact {
  id?: string;
  properties: {
    email?: string;
    phone?: string;
    firstname?: string;
    lastname?: string;
    lead_source?: string;
    first_message?: string;
    createdate?: string;
    hs_object_id?: string;
    lastmodifieddate?: string;
  };
}

interface LeadData {
  type: string;
  value: string;
  conversation_id: string;
  chatbot_public_id: string;
  metadata?: any;
  created_at?: string;
  conversation_messages?: Array<{
    content: string;
    role: string;
    created_at: string;
  }>;
}

export class HubSpotService {
  private config: HubSpotConfig;
  private chatbotPublicId: string;
  private baseUrl = 'https://api.hubapi.com';

  constructor(config: HubSpotConfig, chatbotPublicId: string) {
    this.config = config;
    this.chatbotPublicId = chatbotPublicId;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.config.apiKey}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      throw new Error(`HubSpot API error: ${response.statusText}`);
    }
    
    return response.json();
  }

  async syncLead(leadId: string): Promise<void> {
    try {
      console.log(`Syncing lead ${leadId} to HubSpot...`);
      
      // Récupérer les informations du lead depuis la base de données
      const { data: leadData, error } = await supabase
        .from('conversation_contact_info')
        .select(`
          *,
          conversation:conversation_id(
            messages:conversation_messages(
              content,
              role,
              created_at
            )
          )
        `)
        .eq('id', leadId)
        .single();
      
      if (error) throw error;
      if (!leadData) throw new Error(`Lead with ID ${leadId} not found`);
      
      // Préparer les données pour HubSpot
      const properties: Record<string, string> = {
        lead_source: 'Chatbot',
        first_message: this.getFirstMessage(leadData as any),
        conversation_summary: this.formatConversationHistory(leadData as any)
      };
      
      // Récupérer les métadonnées si elles existent
      let metadata = {};
      try {
        // Requête supplémentaire pour récupérer les métadonnées car elles ne font pas partie du schéma standard
        const { data: metadataResult, error: metadataError } = await supabase
          .from('email_notifications')  // Table qui contient des métadonnées structurées
          .select('metadata')
          .eq('lead_id', leadId)
          .maybeSingle();
          
        if (!metadataError && metadataResult && metadataResult.metadata) {
          metadata = metadataResult.metadata;
          
          // Ajouter des champs supplémentaires si disponibles
          if (metadata.company) {
            properties.company = metadata.company;
          }
          
          if (metadata.name) {
            const nameParts = metadata.name.split(' ');
            properties.firstname = nameParts[0] || '';
            properties.lastname = nameParts.slice(1).join(' ') || 'Unknown';
          }
        }
      } catch (metaErr) {
        console.warn('Error retrieving metadata:', metaErr);
      }
      
      // Ajouter email ou téléphone en fonction du type
      if (leadData.type === 'email') {
        properties.email = leadData.value;
        
        // Si le nom n'est pas encore défini
        if (!properties.lastname) {
          properties.lastname = 'Unknown';
        }
      } else if (leadData.type === 'phone') {
        properties.phone = leadData.value;
        if (!properties.lastname) {
          properties.lastname = 'Unknown';
        }
      }
      
      // Créer le contact dans HubSpot
      const payload = {
        properties: Object.entries(properties).map(([name, value]) => ({
          property: name,
          value
        }))
      };
      
      // Utilisez l'email ou le téléphone comme identifiant principal
      const endpoint = leadData.type === 'email' 
        ? `/contacts/v1/contact/createOrUpdate/email/${encodeURIComponent(leadData.value)}/` 
        : `/contacts/v1/contact/createOrUpdate/phone/${encodeURIComponent(leadData.value)}/`;
      
      const response = await this.makeRequest<{ id: string }>(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      if (!response.id) {
        throw new Error('Failed to create contact in HubSpot');
      }
      
      // Mettre à jour le lead dans la base de données avec l'ID HubSpot
      try {
        const { error: updateError } = await supabase.rpc('update_lead_external_id', {
          p_lead_id: leadId,
          p_external_id: response.id,
          p_processed: true,
          p_metadata: {
            ...metadata,
            hubspot: {
              id: response.id,
              syncedAt: new Date().toISOString()
            }
          }
        });
        
        if (updateError) throw updateError;
      } catch (updateErr) {
        // Fallback si la fonction RPC n'existe pas
        console.warn('Using fallback method for updating lead, RPC function not available:', updateErr);
        
        // Nous pouvons uniquement mettre à jour les champs qui existent dans le schéma
        const { error: updateError } = await supabase
          .from('conversation_contact_info')
          .update({
            processed: true,
            // Nous ne pouvons pas mettre à jour external_id ou metadata ici car ils n'existent pas dans le schéma
          })
          .eq('id', leadId);
          
        if (updateError) throw updateError;
      }
      
      console.log(`Lead ${leadId} successfully synced to HubSpot with ID ${response.id}`);
    } catch (err) {
      console.error('Error syncing lead to HubSpot:', err);
      throw err;
    }
  }
  
  /**
   * Récupère le premier message de l'utilisateur dans la conversation
   */
  private getFirstMessage(leadData: LeadData): string {
    if (!leadData.conversation_messages || leadData.conversation_messages.length === 0) {
      return 'No message history available';
    }
    
    const messages = leadData.conversation_messages
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    
    const firstUserMessage = messages.find(msg => msg.role === 'user');
    return firstUserMessage ? firstUserMessage.content : 'No user message available';
  }
  
  /**
   * Formate l'historique de conversation pour l'inclure dans la description du contact
   */
  private formatConversationHistory(leadData: LeadData): string {
    if (!leadData.conversation_messages || leadData.conversation_messages.length === 0) {
      return `Contact information: ${leadData.value}`;
    }
    
    const messages = leadData.conversation_messages
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .map(msg => {
        const role = msg.role === 'user' ? 'Customer' : 'Chatbot';
        return `[${role}]: ${msg.content}`;
      })
      .join('\n\n');
    
    return `Contact: ${leadData.value}\n\nConversation History:\n${messages}`;
  }

  async syncContactsWebhook(webhookData: any): Promise<void> {
    try {
      const eventType = webhookData.subscriptionType;
      const objectId = webhookData.objectId;

      switch (eventType) {
        case 'contact.creation':
        case 'contact.propertyChange': {
          // Récupérer les détails du contact
          const contact = await this.makeRequest<HubSpotContact>(
            `/crm/v3/objects/contacts/${objectId}?properties=email,phone,firstname,lastname,lead_source,first_message,createdate,lastmodifieddate`
          );
          await this.updateLocalContact(contact);
          break;
        }
        case 'contact.deletion': {
          await this.deleteLocalContact(objectId);
          break;
        }
        case 'contact.restoration': {
          const contact = await this.makeRequest<HubSpotContact>(
            `/crm/v3/objects/contacts/${objectId}?properties=email,phone,firstname,lastname,lead_source,first_message,createdate,lastmodifieddate`
          );
          await this.updateLocalContact(contact);
          break;
        }
      }
    } catch (err) {
      console.error('Error processing HubSpot webhook:', err);
      throw err;
    }
  }

  private async updateLocalContact(contact: HubSpotContact): Promise<void> {
    try {
      const { error } = await supabase
        .from('conversation_contact_info')
        .upsert({
          chatbot_public_id: this.chatbotPublicId,
          type: contact.properties.email ? 'email' : 'phone',
          value: contact.properties.email || contact.properties.phone || '',
          processed: true,
          metadata: {
            hubspot: {
              id: contact.id || contact.properties.hs_object_id,
              firstname: contact.properties.firstname,
              lastname: contact.properties.lastname,
              lead_source: contact.properties.lead_source,
              lastmodifieddate: contact.properties.lastmodifieddate
            }
          }
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error updating local contact:', err);
      throw err;
    }
  }

  private async deleteLocalContact(contactId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('conversation_contact_info')
        .delete()
        .eq('external_id', contactId)
        .eq('chatbot_public_id', this.chatbotPublicId);

      if (error) throw error;
    } catch (err) {
      console.error('Error deleting local contact:', err);
      throw err;
    }
  }
}